import {
  WSEmitType,
  type EmitFunc,
  type EmitMessageFunc,
  type WebsocketOptions,
  type WebsocketStore,
} from '../type';

import {
  PING_INTERVAL,
  PONG_INTERVAL,
  RECONNECT_INTERVAL,
  _events,
  _messageEvents,
} from '../constant';
import { useRef, useState } from 'react';

export const useCreateWebsocket = (options: WebsocketOptions): WebsocketStore => {
  const [isConnected, setIsConnected] = useState(false);

  const ws = useRef<WebSocket | undefined>();

  const forbidReconnect = useRef<boolean>(false);

  const interval = useRef<NodeJS.Timeout>();
  const pingTimeoutId = useRef<NodeJS.Timeout>();
  const pongTimeoutId = useRef<NodeJS.Timeout>();

  const connect = (url: string) => {
    if (ws.current?.OPEN) return;
    if (!url) throw new Error('WS url undefined');

    forbidReconnect.current = false; // update remove forbidReconnect.current

    ws.current = new WebSocket(url);
    ws.current.onopen = (evt) => {
      if (interval) {
        clearInterval(interval.current);
      }

      setIsConnected(true);
      emit(WSEmitType.OPEN, evt);
      heartCheck();
    };
    ws.current.onerror = (evt) => {
      emit(WSEmitType.ERROR, evt);
      reconnect(url);
    };
    ws.current.onclose = (evt) => {
      emit(WSEmitType.CLOSE, evt);
      reconnect(url);
    };
    ws.current.onmessage = (evt) => {
      const data = JSON.parse(evt.data);
      emit(WSEmitType.MESSAGE, evt);
      emitMessage(data?.Type, data);
      heartCheck();
    };
  };

  const heartCheck = () => {
    heartReset();
    if (forbidReconnect.current) return;
    pingTimeoutId.current = setTimeout(() => {
      if (!ws.current) return;
      if (ws.current.readyState !== 1) return;
      ws.current.send('heartbeat');
      pongTimeoutId.current = setTimeout(() => {
        if (!ws.current) return;
        if (ws.current.readyState !== 1) return;
        ws.current.close();
        ws.current = undefined;
      }, PONG_INTERVAL);
    }, PING_INTERVAL);
  };

  const heartReset = () => {
    if (pingTimeoutId) {
      clearTimeout(pingTimeoutId.current);
    }
    if (pongTimeoutId) {
      clearTimeout(pongTimeoutId.current);
    }
  };

  const reconnect: WebsocketStore['reconnect'] = (url: string) => {
    if (interval) {
      clearInterval(interval.current);
    }
    if (forbidReconnect.current) {
      return;
    }
    if (options.reconnectEnabled) {
      interval.current = setInterval(() => {
        ws.current = undefined;
        connect(url);
      }, options.reconnectInterval ?? RECONNECT_INTERVAL);
    }
  };

  const emit: EmitFunc = (name, data) => {
    if (!_events[name]) {
      return;
    }

    const fireCallbacks = (callback: (args: any) => void) => {
      callback(data);
    };

    _events[name].forEach(fireCallbacks);
  };

  const emitMessage: EmitMessageFunc = (name, data) => {
    if (!_messageEvents[name]) {
      return;
    }
    const fireCallbacks = (callback: (args: any) => void) => {
      if (data.Data) {
        callback(data);
      } else {
        throw new Error('Websocket incorrect format');
      }
    };

    _messageEvents[name].forEach(fireCallbacks);
  };

  const disconnect = () => {
    forbidReconnect.current = true;
    ws.current?.close();
    ws.current = undefined;
  };

  const sendMessage: WebsocketStore['sendMessage'] = (message: string | Blob | BufferSource) => {
    ws.current?.send(message);
  };

  const sendObject: WebsocketStore['sendObject'] = (data) => {
    ws.current?.send(JSON.stringify(data));
  };

  const on: WebsocketStore['on'] = (key, cb) => {
    _events[key] = _events[key] ?? [];
    _events[key].push(cb);
    return cb;
  };
  const removeListener: WebsocketStore['removeListener'] = (type, cb) => {
    if (!_events[type]) {
      return;
    }
    const filterListeners = (listener: any) => listener !== cb;
    _events[type] = _events[type].filter(filterListeners);
  };

  const onMessage: WebsocketStore['onMessage'] = (key, cb) => {
    _messageEvents[key] = _messageEvents[key] ?? [];
    _messageEvents[key].push(cb);
    return cb;
  };

  const removeMessageListener: WebsocketStore['removeMessageListener'] = (key, cb) => {
    if (!_messageEvents[key]) {
      return;
    }
    const filterListeners = (listener: any) => listener !== cb;
    _messageEvents[key] = _messageEvents[key].filter(filterListeners);
  };

  const offMessage: WebsocketStore['offMessage'] = (key, cb) => {
    if (!_messageEvents[key]) {
      return;
    }
    const filterListeners = (listener: any) => listener !== cb;
    _messageEvents[key] = _messageEvents[key].filter(filterListeners);
  };

  return {
    isConnected,
    on,
    connect,
    reconnect,
    onMessage,
    disconnect,
    sendObject,
    offMessage,
    sendMessage,
    removeListener,
    removeMessageListener,
  };
};
